import { useRef, useEffect } from 'react';

// ── Quaternion → mat3 (column-major Float32Array for WebGL) ──
function qToMat3([w, x, y, z]) {
  return new Float32Array([
    1 - 2 * (y * y + z * z), 2 * (x * y + w * z), 2 * (x * z - w * y),
    2 * (x * y - w * z), 1 - 2 * (x * x + z * z), 2 * (y * z + w * x),
    2 * (x * z + w * y), 2 * (y * z - w * x), 1 - 2 * (x * x + y * y),
  ]);
}

function qMul([w1, x1, y1, z1], [w2, x2, y2, z2]) {
  return [
    w1 * w2 - x1 * x2 - y1 * y2 - z1 * z2,
    w1 * x2 + x1 * w2 + y1 * z2 - z1 * y2,
    w1 * y2 - x1 * z2 + y1 * w2 + z1 * x2,
    w1 * z2 + x1 * y2 - y1 * x2 + z1 * w2,
  ];
}

// Isometric pose: matches the static logo silhouette exactly.
const RX_ISO = Math.atan(1 / Math.sqrt(2));   // ~35.264°
const RY_ISO = Math.PI / 4;                   // 45°

// Rotation timeline: starts at iso pose, holds briefly, then eases up to a
// steady spin around the Y axis. Speed ramps linearly over RAMP seconds, so
// the angle is quadratic during ramp and linear after.
const HOLD = 0.6;        // seconds of stillness before motion begins
const RAMP = 5.0;        // seconds to reach full speed
const MAX_SPEED = 0.32;  // rad/s at full speed (~20s per revolution)

// Per-mount randomisation so each reload tumbles differently while still
// starting at the exact iso pose (all wobble terms are 0 at theta=0 thanks to
// the (sin(... + phase) - sin(phase)) construction).
function makeRandomMotion() {
  const rand = (a, b) => a + Math.random() * (b - a);
  const ydir = Math.random() < 0.5 ? -1 : 1;
  return {
    ydir,
    rx: [
      { amp: 0.40, freq: rand(0.55, 0.85), phase: rand(0, Math.PI * 2) },
      { amp: 0.12, freq: rand(1.10, 1.55), phase: rand(0, Math.PI * 2) },
    ],
    rz: [
      { amp: 0.18, freq: rand(0.30, 0.55), phase: rand(0, Math.PI * 2) },
      { amp: 0.08, freq: rand(0.75, 1.10), phase: rand(0, Math.PI * 2) },
    ],
  };
}

function rotationQuat(elapsed, m, extraRx = 0) {
  // Cumulative spin angle: 0 during HOLD, quadratic ramp-up, then linear.
  let theta;
  const t = Math.max(0, elapsed - HOLD);
  if (t < RAMP) {
    theta = MAX_SPEED * t * t / (2 * RAMP);
  } else {
    theta = MAX_SPEED * (t - RAMP / 2);
  }

  // Three-axis rotation. Y is the primary spin (random direction). X and Z
  // are bounded wobbles whose phase is randomised but offset-corrected so
  // they still evaluate to 0 at theta=0.
  const wobble = (terms) => terms.reduce((s, w) =>
    s + w.amp * (Math.sin(w.freq * theta + w.phase) - Math.sin(w.phase)), 0);

  const ry = RY_ISO + m.ydir * theta;
  const rx = RX_ISO + extraRx + wobble(m.rx);
  const rz = wobble(m.rz);

  // Compose: Rx · Ry · Rz
  const qx = [Math.cos(rx / 2), Math.sin(rx / 2), 0, 0];
  const qy = [Math.cos(ry / 2), 0, Math.sin(ry / 2), 0];
  const qz = [Math.cos(rz / 2), 0, 0, Math.sin(rz / 2)];
  return qMul(qx, qMul(qy, qz));
}

const VS = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`;

// Gradient mapping derived from kuutio-cube-rotating.svg:
//   front face: linear-gradient(135deg, #367ABA, #73C92D, #E8114B)  → diagonal blue→green→red
//   right face: linear-gradient(135deg, white,   #75CFEB, #008549)  → diagonal white→cyan→green
//   top face:   linear-gradient(to top,  #FF9F37, #367ABA)          → orange (front of diamond) to blue (back of diamond)
//
// At iso pose (rx=atan(1/√2), ry=45°): visible faces are +Y (top), -X (left of screen), +Z (right of screen).
const FS = `
precision highp float;
uniform vec2 uRes;
uniform mat3 uRot;

mat3 tr(mat3 m){return mat3(m[0][0],m[1][0],m[2][0],m[0][1],m[1][1],m[2][1],m[0][2],m[1][2],m[2][2]);}

float sdBox(vec3 p, vec3 b, float r){
  vec3 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0) - r;
}

vec3 mix3(vec3 a, vec3 b, vec3 c, float t){
  t = clamp(t, 0.0, 1.0);
  return t < 0.5 ? mix(a, b, t * 2.0) : mix(b, c, (t - 0.5) * 2.0);
}

// Quadratic Bezier 3-stop blend: B is a control point, not a hard stop —
// gradient passes THROUGH B's color as a tinge (50% at t=0.5) instead of peaking,
// so the middle stop reads as a smooth band rather than a sharp stripe.
vec3 mix3smooth(vec3 a, vec3 b, vec3 c, float t){
  t = clamp(t, 0.0, 1.0);
  float u = 1.0 - t;
  return u * u * a + 2.0 * u * t * b + t * t * c;
}

vec3 faceColor(vec3 p, vec3 n){
  // sRGB stops
  const vec3 BLUE   = vec3(0.2118, 0.4784, 0.7294); // #367ABA
  const vec3 ORANGE = vec3(1.0,    0.7569, 0.2235); // #FFC139 — yellow tip
  const vec3 GREEN_L= vec3(0.4510, 0.7882, 0.1765); // #73C92D
  const vec3 RED    = vec3(0.9098, 0.0667, 0.2941); // #E8114B
  const vec3 WHITE  = vec3(1.0);
  const vec3 CYAN   = vec3(0.3725, 0.8980, 0.9804); // #5FE5FA — brighter than #75CFEB
  const vec3 GREEN_R= vec3(0.0,    0.5216, 0.2863); // #008549

  vec3 an = abs(n);
  if (an.y >= an.x && an.y >= an.z) {
    // ±Y faces — diagonal: yellow at top vertex of iso diamond, blue at bottom vertex.
    // pow(t, 1.6) biases the gradient so yellow holds longer before transitioning.
    float t = 0.5 + 0.5 * (p.z - p.x);
    return mix(ORANGE, BLUE, pow(clamp(t, 0.0, 1.0), 1.6));
  } else if (an.x >= an.z) {
    // ±X faces (logo "left") — diagonal: blue at lower-left silhouette, red at inner-V apex.
    // Smooth 3-stop (Bezier) so green reads as a soft band, not a hard stripe.
    float t = 0.5 + 0.5 * (p.y + p.z);
    return mix3smooth(BLUE, GREEN_L, RED, t);
  } else {
    // ±Z faces (logo "right") — diagonal: white at top corner, green at bottom corner
    float t = 0.5 - 0.5 * (p.x + p.y);
    return mix3(WHITE, CYAN, GREEN_R, t);
  }
}

vec3 calcN(vec3 p){
  vec2 e = vec2(0.001, 0.0);
  return normalize(vec3(
    sdBox(p + e.xyy, vec3(0.5), 0.05) - sdBox(p - e.xyy, vec3(0.5), 0.05),
    sdBox(p + e.yxy, vec3(0.5), 0.05) - sdBox(p - e.yxy, vec3(0.5), 0.05),
    sdBox(p + e.yyx, vec3(0.5), 0.05) - sdBox(p - e.yyx, vec3(0.5), 0.05)
  ));
}

void main(){
  // Orthographic projection — matches SVG which has no perspective
  vec2 uv = (gl_FragCoord.xy - uRes * 0.5) / min(uRes.x, uRes.y);
  vec3 ro = vec3(uv * 2.0, 2.8);
  vec3 rd = vec3(0.0, 0.0, -1.0);

  mat3 R = uRot, iR = tr(R);
  vec3 o = iR * ro;
  vec3 d = iR * rd;

  float t = 0.0, mn = 999.0, tmn = 0.0;
  bool hit = false;
  for (int i = 0; i < 80; i++) {
    float dd = sdBox(o + d * t, vec3(0.5), 0.05);
    if (dd < mn) { mn = dd; tmn = t; }
    if (dd < 0.0004) { hit = true; break; }
    t += max(dd, 0.001);
    if (t > 8.0) break;
  }

  // Silhouette antialiasing. The cube is drawn by this shader, not by
  // triangles, so the context's MSAA never touches its edge: at 72 px the
  // rim crawled as it turned. The march's closest approach (mn) is the
  // ray's distance to the surface in world units; one pixel is 2/min(res)
  // of those (uv spans ±1 across the short side). A miss within a pixel
  // is drawn as the nearest surface point, faded by how far it missed.
  float px = 2.0 / min(uRes.x, uRes.y);
  float cover = 1.0;
  if (!hit) {
    cover = 1.0 - smoothstep(0.0, px, mn);
    if (cover <= 0.0) { gl_FragColor = vec4(0.0); return; }
    t = tmn;
  }

  vec3 p = o + d * t;
  vec3 n = calcN(p);

  // Pick gradient by the dominant axis of the sharp inner cube (otherwise the
  // rounded corner blends face normals and gives muddy mixed colors).
  vec3 sharpAxis = step(abs(p.yzx), abs(p.xyz)) * step(abs(p.zxy), abs(p.xyz)) * sign(p);
  vec3 color = faceColor(p, sharpAxis);

  // Very subtle 3D shading — keep colors vivid
  vec3 wn = R * n;
  vec3 ld = normalize(vec3(-0.4, 0.85, 0.5));
  float diff = max(dot(wn, ld), 0.0);
  color *= 0.90 + 0.10 * diff;

  // Premultiplied, to match the ONE / ONE_MINUS_SRC_ALPHA blend.
  gl_FragColor = vec4(color * cover, cover);
}`;

/**
 * @param {object} spinRef optional ref { rate } — rotation-time multiplier
 *   read every frame (1 = normal, negative = reversed). Lets physics-driven
 *   contexts couple tumble speed/direction to movement without re-rendering.
 */
export default function KuutioCubeGradient({ size = 280, spinRef = null }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true });
    if (!gl) return;

    function mkShader(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
        console.error('Shader:', gl.getShaderInfoLog(s));
      return s;
    }

    const vs = mkShader(gl.VERTEX_SHADER, VS);
    const fs = mkShader(gl.FRAGMENT_SHADER, FS);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
      console.error('Link:', gl.getProgramInfoLog(prog));
    gl.useProgram(prog);

    const uRes = gl.getUniformLocation(prog, 'uRes');
    const uRot = gl.getUniformLocation(prog, 'uRot');

    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const motion = makeRandomMotion();
    let simTime = 0;
    let xAngle = 0; // accumulating x-tumble (spinRef.xRate) — full-3D roll
    let last = performance.now();

    function render() {
      if (!visibleRef.current) { rafRef.current = requestAnimationFrame(render); return; }
      const now = performance.now();
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      simTime += dt * (spinRef?.current?.rate ?? 1);
      xAngle += dt * (spinRef?.current?.xRate ?? 0);
      const rot = qToMat3(rotationQuat(simTime, motion, xAngle));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniformMatrix3fv(uRot, false, rot);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      rafRef.current = requestAnimationFrame(render);
    }

    rafRef.current = requestAnimationFrame(render);

    const observer = new IntersectionObserver(
      ([entry]) => { visibleRef.current = entry.isIntersecting; },
      { threshold: 0 },
    );
    observer.observe(canvas);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.deleteBuffer(vbo);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [size]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: 'block' }} />;
}
